import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Editor, Extension, Range } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';

import { cn } from '@/components/ui/cn';

/** One command in the "/" (and "{") widget menu. */
export interface SlashItem {
  title: string;
  hint: string;
  keywords: string;
  glyph: string;
  command: (p: { editor: Editor; range: Range }) => void;
}

/** The widget palette. Each command replaces the trigger text (deleteRange) then inserts the block. */
function slashItems(onImage: () => void, onEmbed: () => void): SlashItem[] {
  const set = (fn: (c: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>) => ({ editor, range }: { editor: Editor; range: Range }) => fn(editor.chain().focus().deleteRange(range)).run();
  return [
    { title: 'Text', hint: 'Plain paragraph', keywords: 'paragraph body text', glyph: '¶', command: set((c) => c.setParagraph()) },
    { title: 'Heading 1', hint: 'Big section heading', keywords: 'h1 title big', glyph: 'H₁', command: set((c) => c.toggleHeading({ level: 1 })) },
    { title: 'Heading 2', hint: 'Medium heading', keywords: 'h2 subtitle', glyph: 'H₂', command: set((c) => c.toggleHeading({ level: 2 })) },
    { title: 'Heading 3', hint: 'Small heading', keywords: 'h3', glyph: 'H₃', command: set((c) => c.toggleHeading({ level: 3 })) },
    { title: 'Bulleted list', hint: 'A simple bullet list', keywords: 'unordered ul bullet', glyph: '•', command: set((c) => c.toggleBulletList()) },
    { title: 'Numbered list', hint: 'An ordered list', keywords: 'ordered ol number', glyph: '1.', command: set((c) => c.toggleOrderedList()) },
    { title: 'Checklist', hint: 'Track tasks with checkboxes', keywords: 'todo task checkbox check', glyph: '☑', command: set((c) => c.toggleTaskList()) },
    { title: 'Radio group', hint: 'Single-choice options', keywords: 'radio single choice option select poll', glyph: '◉', command: set((c) => c.insertContent({ type: 'radioGroup', content: [{ type: 'radioItem', attrs: { checked: true }, content: [{ type: 'paragraph' }] }, { type: 'radioItem', attrs: { checked: false }, content: [{ type: 'paragraph' }] }] })) },
    { title: 'Table', hint: '3×3 table with header', keywords: 'grid rows columns', glyph: '▦', command: set((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true })) },
    { title: 'Quote', hint: 'Capture a quotation', keywords: 'blockquote citation', glyph: '❝', command: set((c) => c.toggleBlockquote()) },
    { title: 'Code block', hint: 'Formatted code', keywords: 'code pre snippet', glyph: '</>', command: set((c) => c.toggleCodeBlock()) },
    { title: 'Divider', hint: 'A horizontal rule', keywords: 'hr separator line', glyph: '—', command: set((c) => c.setHorizontalRule()) },
    { title: 'Checklist progress', hint: 'A live completion bar for this page', keywords: 'progress completion percent tasks bar', glyph: '▰', command: set((c) => c.insertContent({ type: 'checklistProgress' })) },
    { title: 'Image / video', hint: 'Upload and embed media', keywords: 'media picture photo file upload', glyph: '🖼', command: ({ editor, range }) => { editor.chain().focus().deleteRange(range).run(); onImage(); } },
    { title: 'Embed', hint: 'Link a board item or whiteboard', keywords: 'reference item whiteboard card link', glyph: '🔗', command: ({ editor, range }) => { editor.chain().focus().deleteRange(range).run(); onEmbed(); } },
  ];
}

interface ListHandle {
  onKeyDown: (p: SuggestionKeyDownProps) => boolean;
}

const CommandList = forwardRef<ListHandle, SuggestionProps<SlashItem>>((props, ref) => {
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => setSel(0), [props.items]);
  // Keep the keyboard-highlighted item visible as it moves past the scroll edge.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${sel}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const pick = (i: number) => {
    const item = props.items[i];
    if (item) props.command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') { setSel((s) => (s + props.items.length - 1) % props.items.length); return true; }
      if (event.key === 'ArrowDown') { setSel((s) => (s + 1) % props.items.length); return true; }
      if (event.key === 'Enter') { pick(sel); return true; }
      return false;
    },
  }));

  if (props.items.length === 0) return <div className="w-[280px] rounded-xl border border-line bg-surface-raised p-3 text-[12px] text-fg-faint shadow-lg backdrop-blur-xl">No matching widgets</div>;

  return (
    <div ref={listRef} className="max-h-[320px] w-[280px] overflow-auto rounded-xl border border-line bg-surface-raised p-1.5 shadow-xl">
      {props.items.map((item, i) => (
        <button
          key={item.title}
          type="button"
          data-idx={i}
          onMouseEnter={() => setSel(i)}
          onMouseDown={(e) => { e.preventDefault(); pick(i); }}
          className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors', i === sel ? 'bg-layer-strong' : 'hover:bg-layer')}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-inset text-[13px] text-fg-soft ring-1 ring-line">{item.glyph}</span>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-medium text-fg-strong">{item.title}</span>
            <span className="block truncate text-[11px] text-fg-faint">{item.hint}</span>
          </span>
        </button>
      ))}
    </div>
  );
});
CommandList.displayName = 'CommandList';

/** Positions a floating menu at the caret, following it as the query changes; no tippy dependency. */
function makeRenderer() {
  let component: ReactRenderer<ListHandle, SuggestionProps<SlashItem>> | null = null;
  let el: HTMLDivElement | null = null;

  const place = (rect: (() => DOMRect | null) | null | undefined) => {
    if (!el || !rect) return;
    const r = rect();
    if (!r) return;
    el.style.top = `${r.bottom + 6}px`;
    el.style.left = `${Math.min(r.left, window.innerWidth - 296)}px`;
  };

  return {
    onStart: (props: SuggestionProps<SlashItem>) => {
      component = new ReactRenderer(CommandList, { props, editor: props.editor });
      el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.zIndex = '120';
      document.body.appendChild(el);
      el.appendChild(component.element);
      place(props.clientRect);
    },
    onUpdate: (props: SuggestionProps<SlashItem>) => {
      component?.updateProps(props);
      place(props.clientRect);
    },
    onKeyDown: (props: SuggestionKeyDownProps) => {
      if (props.event.key === 'Escape') { el?.remove(); el = null; component?.destroy(); component = null; return true; }
      return component?.ref?.onKeyDown(props) ?? false;
    },
    onExit: () => {
      el?.remove();
      el = null;
      component?.destroy();
      component = null;
    },
  };
}

/**
 * The "/"-and-"{" widget menu. Typing either character opens an inline autocomplete of blocks to
 * insert (headings, lists, checklist, table, quote, code, divider, media, embed) — Notion/Confluence
 * style. Two triggers are registered because the user asked for "{"; "/" is the common convention.
 */
export const SlashCommand = Extension.create<{ onImage: () => void; onEmbed: () => void }>({
  name: 'slashCommand',
  addOptions() {
    return { onImage: () => {}, onEmbed: () => {} };
  },
  addProseMirrorPlugins() {
    const items = ({ query }: { query: string }) => {
      const q = query.toLowerCase();
      return slashItems(this.options.onImage, this.options.onEmbed).filter((i) => i.title.toLowerCase().includes(q) || i.keywords.includes(q));
    };
    const command = ({ editor, range, props }: { editor: Editor; range: Range; props: SlashItem }) => props.command({ editor, range });
    return ['/', '{'].map((char) =>
      Suggestion<SlashItem>({
        editor: this.editor,
        char,
        pluginKey: new PluginKey(`slash-${char === '/' ? 'slash' : 'brace'}`),
        allowSpaces: false,
        startOfLine: false,
        items,
        command,
        render: makeRenderer,
      }),
    );
  },
});
